import { apiRequest } from "./api";

export type LedgerEventType =
  | "PAYROLL_FINALIZED"
  | "PAYROLL_NET_DISBURSEMENT"
  | "PAYROLL_EMPLOYER_CONTRIBUTIONS"
  | "LOAN_DISBURSED"
  | "LOAN_INSTALLMENT"
  | "CASH_ADVANCE_RELEASED"
  | "CASH_ADVANCE_REPAID"
  | "WALLET_COMMISSION"
  | "WALLET_ENCASHMENT"
  | "WALLET_ADVANCE_RELEASED"
  | "WALLET_ADVANCE_REPAID"
  | "WALLET_CREDIT_ADDED"
  | "STORE_RECORD_CREATED"
  | "STORE_RECORD_DELETED"
  | "STORE_EXPENSE"
  | "STORE_EXPENSE_DELETED"
  | "STORE_EXPENSE_UPDATED"
  | "STORE_INCOME"
  | "STORE_INCOME_DELETED"
  | "STORE_INCOME_UPDATED"
  | "SERVICE_REQUEST_APPROVED"
  | "SERVICE_REQUEST_VOIDED"
  | "MANUAL_ENTRY"
  | "ADJUSTMENT_APPLIED";

export interface LedgerEvent {
  eventId: string;
  businessId: string;
  userId?: string;
  employeeName?: string;
  type: LedgerEventType;
  description: string;
  amount: number;
  refId: string;
  refType: string;
  period?: string;
  adjustmentDirection?: "DEDUCTION" | "ADDITION";
  manualDirection?: "INCOME" | "EXPENSE";
  eventDate?: string;
  createdAt: string;
  createdBy?: string;
}

export interface LedgerQueryParams {
  businessId?: string;
  from?: string;
  to?: string;
  type?: LedgerEventType;
  userId?: string;
  limit?: number;
  lastKey?: string;
}

export interface LedgerResponse {
  items: LedgerEvent[];
  nextKey?: string;
}

export interface LedgerSummary {
  totalOutflow: number;
  totalInflow: number;
  net: number;
  eventCount: number;
  byBusiness?: Record<string, { outflow: number; inflow: number; net: number; count: number }>;
}

function buildLedgerQs(params: Omit<LedgerQueryParams, "lastKey"> & { lastKey?: string }): string {
  const q = new URLSearchParams();
  if (params.businessId) q.set("businessId", params.businessId);
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.type) q.set("type", params.type);
  if (params.userId) q.set("userId", params.userId);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.lastKey) q.set("lastKey", params.lastKey);
  return q.toString();
}

export const ledgerApi = {
  list: async (params: LedgerQueryParams): Promise<LedgerResponse> =>
    apiRequest<LedgerResponse>(`/admin/ledger?${buildLedgerQs(params)}`),

  summary: async (params: Omit<LedgerQueryParams, "limit" | "lastKey">): Promise<LedgerSummary> =>
    apiRequest<LedgerSummary>(`/admin/ledger/summary?${buildLedgerQs(params)}`),

  createManualEntry: async (payload: {
    businessId: string;
    eventDate: string;
    manualDirection: "INCOME" | "EXPENSE";
    amount: number;
    description: string;
  }): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>("/admin/ledger", {
      method: "POST",
      body: payload,
    });
  },
};
